import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Stack } from '@mui/material';
import DataDisplay, { DataDisplayVariant } from '@sharedCR/coreDesignSystem/DataDisplay';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import _ from 'lodash';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { ConfirmationModalType } from 'constants/confirmationDialog';
import { FeatureFlag } from 'constants/featureFlags';
import { OptimalBlueAdjustmentType } from 'constants/loanApplication';
import { modifiedPendingChanges } from 'constants/pendingChanges';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';
import { getPendingChangeKind } from 'utils/pendingChanges';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { openConfirmationDialogAction } from 'store/global/actions';
import { getLockExtensionSettingsAction } from 'store/loanApplications/actions';
import {
  selectFormattedLoanAndLockEditAdjustments,
  selectIsLoanLenderEngineType,
  selectIsLoanLocked,
  selectIsLoanOptimalBlueOrSifterEngineType,
  selectLockInfoSummaryData,
} from 'store/loanApplications/selectors';
import { getCustomAdjustmentsAction, getLLPATypesAction } from 'store/programs/actions';
import { selectIsCustomAdjustmentsLoaded } from 'store/programs/selectors';

import { lockInfoMenuId } from '../../AdditionalComponents/LockInfoAdditionalInfo';
import { pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';
import { ContentHeadline } from '../sharedComponents';

import AddActionButton from './AddActionButton';
import AddCustomAdjustmentForm from './AddCustomAdjustmentsForm';
import AddExtensionForm from './AddExtensionForm';
import {
  dataDisplayConfig,
  getAdjustmentsTableConfig,
  getTotalAmountField,
  markupsTableConfig,
} from './LockInfo.config';
import { CustomAdjustmentType } from './LockInfo.constants';
import { adjustmentsContainerStyles, blockCustomStyles } from './LockInfo.styles';
import { prepareFormData, prepareTableData } from './LockInfo.utils';

const LockInfo = ({
  pendingChanges,
  isLoading: isGlobalLoading,
  isSaving,
  applicationData,
  entitySideData,
  setEntitySideData,
  setInitialEntitySideData,
}) => {
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const isLoading = (isGlobalLoading || isLocalLoading) && !isSaving;

  const dispatch = useDispatch();

  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);

  const loanId = useLoanId();

  const lockInfo = useSelector(selectLockInfoSummaryData);
  const initialAdjustments = useSelector(selectFormattedLoanAndLockEditAdjustments);
  const isCustomAdjustmentsLoaded = useSelector(selectIsCustomAdjustmentsLoaded);
  const isLoanLocked = useSelector(selectIsLoanLocked);
  const isLenderEngineType = useSelector(selectIsLoanLenderEngineType);
  const isOBEngineType = useSelector(selectIsLoanOptimalBlueOrSifterEngineType);
  const canViewMarkupLevels = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.VIEW_MARKUP_LEVELS));

  const isOBExternalLoanLock = isOBEngineType && isExternalLoanLockEnabled;

  const [actionType, setActionType] = useState(null);

  const { adjustments } = entitySideData;
  const { markups } = applicationData;

  const menuContainer = document.getElementById(lockInfoMenuId);

  const isActionActive = !!actionType;

  useEffect(() => {
    const isAdjustmentsExist = !!initialAdjustments;

    if (!isAdjustmentsExist || (!isCustomAdjustmentsLoaded && !isOBEngineType)) {
      return;
    }

    const isAdjustmentsEntitySideDataExist = _.has(entitySideData, 'adjustments');

    if (isAdjustmentsEntitySideDataExist) {
      return;
    }

    setEntitySideData(prevState => ({
      ...prevState,
      adjustments: initialAdjustments,
    }));
    setInitialEntitySideData(prevState => ({
      ...prevState,
      adjustments: initialAdjustments,
    }));
  }, [
    initialAdjustments,
    entitySideData,
    setEntitySideData,
    setInitialEntitySideData,
    isCustomAdjustmentsLoaded,
    isOBEngineType,
  ]);

  const { adjustmentFullTableData, rateAdjustmentFullTableData } = useMemo(() => {
    const adjustmentsTableDataWithPendingChanges = adjustments
      ? prepareTableData(adjustments, pendingChanges)
      : [];

    const [rateAdjustments, priceAdjustments] = _.partition(
      adjustmentsTableDataWithPendingChanges,
      ({ originalType }) => originalType === OptimalBlueAdjustmentType.RATE,
    );

    return {
      adjustmentFullTableData: [
        ...priceAdjustments,
        ...getConditionalArray(!isOBExternalLoanLock, () => [
          getTotalAmountField(priceAdjustments),
        ]),
      ],
      rateAdjustmentFullTableData: [
        ...rateAdjustments,
        ...getConditionalArray(rateAdjustments.length > 0 && !isOBExternalLoanLock, () => [
          getTotalAmountField(rateAdjustments),
        ]),
      ],
    };
  }, [adjustments, pendingChanges, isOBExternalLoanLock]);

  const hasCustomAdjustmentsPendingChanges = useMemo(() => {
    return (
      !adjustments ||
      adjustmentFullTableData.some(
        item =>
          item.type === CustomAdjustmentType.CUSTOM_ADJUSTMENT &&
          (modifiedPendingChanges.includes(item.pendingChangesActionType) || item.isAdded),
      )
    );
  }, [adjustmentFullTableData, adjustments]);

  const { customAdjustmentsTypeIds, currentExtensionsTermsSum } = useMemo(
    () =>
      (adjustments || []).reduce(
        (acc, { type, customAdjustmentTypeId, id, term }) => {
          if (type === CustomAdjustmentType.CUSTOM_ADJUSTMENT) {
            acc.customAdjustmentsTypeIds.push(customAdjustmentTypeId || id);
          }

          if (type === CustomAdjustmentType.EXTENSION) {
            acc.currentExtensionsTermsSum = acc.currentExtensionsTermsSum + term;
          }

          return acc;
        },
        { customAdjustmentsTypeIds: [], currentExtensionsTermsSum: 0 },
      ),
    [adjustments],
  );

  const sessionInfo = useMemo(() => {
    if (!adjustments) {
      return null;
    }

    return adjustments.reduce(
      (acc, item) => {
        const isItemFit = item.isDeleted || item.isAdded;

        if (isItemFit) {
          if (!acc.type) {
            acc.type = item.type;
          }

          acc[item.isAdded ? 'addedItems' : 'deletedItems'].push(item);
        }

        return acc;
      },
      {
        type: null,
        addedItems: [],
        deletedItems: [],
      },
    );
  }, [adjustments]);

  const onDelete = useCallback(
    ({ id }) => {
      setEntitySideData(prevState => ({
        ...prevState,
        adjustments: prevState.adjustments.reduce((acc, entity) => {
          if (entity.id !== id) {
            acc.push(entity);

            return acc;
          }

          if (entity.isAdded) {
            return acc;
          }

          acc.push({ ...entity, isDeleted: true });

          return acc;
        }, []),
      }));
    },
    [setEntitySideData],
  );

  const adjustmentsTableConfig = useMemo(() => {
    return getAdjustmentsTableConfig({
      sessionInfo,
      onDelete,
      isActionActive,
      isOBExternalLoanLock,
    });
  }, [sessionInfo, onDelete, isActionActive, isOBExternalLoanLock]);

  useEffectSafeStrictMode(() => {
    setIsLocalLoading(true);

    Promise.all([
      dispatch(getLLPATypesAction()),
      ...getConditionalArray(isLenderEngineType, () => [dispatch(getCustomAdjustmentsAction())]),
    ]).finally(() => setIsLocalLoading(false));
  }, [dispatch, isLenderEngineType]);

  const cancelForm = useCallback(() => {
    setActionType(null);
  }, []);

  const saveForm = useCallback(
    ({ form, type }) => {
      const dataToSave = prepareFormData({ form, type, isExternal: isOBEngineType });

      setEntitySideData(prevState => ({
        ...prevState,
        adjustments: [...prevState.adjustments, dataToSave],
      }));
      setActionType(null);
    },
    [setEntitySideData, isOBEngineType],
  );

  const onMenuClick = useCallback(e => {
    const { actiontype } = e.currentTarget.dataset;

    setActionType(actiontype);
  }, []);

  const onAddFloatDown = useCallback(() => {
    setIsDisabled(true);

    dispatch(getLockExtensionSettingsAction({ loanId }))
      .then(({ payload }) => {
        const { floatDownSetting } = payload || {};

        if (!floatDownSetting) {
          return dispatch(
            openConfirmationDialogAction({
              message: 'Add Float Down',
              description: 'There is no float down setting',
              type: ConfirmationModalType.ERROR,
            }),
          );
        }

        const { cost } = floatDownSetting;
        const dataToSave = prepareFormData({
          type: CustomAdjustmentType.FLOAT_DOWN,
          cost,
        });

        setEntitySideData(prevState => ({
          ...prevState,
          adjustments: [...prevState.adjustments, dataToSave],
        }));
      })
      .catch(Error)
      .finally(() => setIsDisabled(false));
  }, [dispatch, loanId, setEntitySideData]);

  return (
    <Box sx={boxStyle}>
      <ContentHeadline title="Lock Info" />
      <Stack className="gap-40">
        <Stack className="flex-1 gap-12 items-start justify-between" direction="row">
          {dataDisplayConfig.map(({ key, ...item }) => (
            <DataDisplay
              key={key}
              {...item}
              changeKind={getPendingChangeKind(pendingChanges[key])}
              isLoading={isLoading}
              value={lockInfo[key]}
              variant={DataDisplayVariant.STYLE_B}
            />
          ))}
        </Stack>

        <Box sx={adjustmentsContainerStyles}>
          <TableCDS
            withNoAvailableMessage
            config={adjustmentsTableConfig}
            customStyles={blockCustomStyles}
            data={adjustmentFullTableData}
            isLoading={isLoading}
            title="Adjustments"
          />
          {actionType === CustomAdjustmentType.CUSTOM_ADJUSTMENT && (
            <AddCustomAdjustmentForm
              cancelForm={cancelForm}
              customAdjustmentsTypeIds={customAdjustmentsTypeIds}
              isLoading={isLoading}
              loanId={loanId}
              saveForm={saveForm}
              setIsLocalLoading={setIsLocalLoading}
            />
          )}
          {actionType === CustomAdjustmentType.EXTENSION && (
            <AddExtensionForm
              cancelForm={cancelForm}
              currentExtensionsTermsSum={currentExtensionsTermsSum}
              isLoading={isLoading}
              saveForm={saveForm}
              setIsLocalLoading={setIsLocalLoading}
            />
          )}
        </Box>

        {!!rateAdjustmentFullTableData.length && (
          <TableCDS
            withNoAvailableMessage
            config={adjustmentsTableConfig}
            customStyles={blockCustomStyles}
            data={rateAdjustmentFullTableData}
            isLoading={isLoading}
            title="Rate Adjustments"
          />
        )}

        {isOBEngineType && canViewMarkupLevels && (
          <TableCDS
            withNoAvailableMessage
            config={markupsTableConfig}
            customStyles={blockCustomStyles}
            data={markups}
            isLoading={isLoading}
            title="Markup Levels"
          />
        )}
      </Stack>

      {!!menuContainer && (
        <>
          {createPortal(
            <AddActionButton
              hasCustomAdjustmentsPendingChanges={hasCustomAdjustmentsPendingChanges}
              isDisabled={isDisabled || isActionActive || isSaving || !isLoanLocked}
              sessionInfo={sessionInfo}
              onAddFloatDown={onAddFloatDown}
              onClick={onMenuClick}
            />,
            menuContainer,
          )}
        </>
      )}
    </Box>
  );
};

LockInfo.propTypes = pagesPropTypes;

export default LockInfo;
